import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, Chip, Tooltip,
  TextField, Collapse, CircularProgress, Tabs, Tab,
  InputAdornment, Popover, LinearProgress, InputBase,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  WbSunnyOutlined as MorgenIcon,
  InboxOutlined as BacklogIcon,
  CheckBoxOutlined as TodoIcon,
  StickyNote2Outlined as NoteIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { schrijfTaak } from '../../utils/planningBronnen';
import { leesJson, meldFout } from '../../utils/apiFout';
import { fetchDayEvents } from '../../utils/graphCalendar';
import PageHeader from '../../ui-components/PageHeader';
import TaakPaneel from '../../ui-components/TaakPaneel/TaakPaneel';
import { formatDateISO } from '../../utils/weekUtils';
import { AFGEROND_SET, NIET_PRODUCTIE_SET } from '../../utils/taakStatus';
import {
  initMicrosoftTodoService,
  getMyTodos,
  completeTask as completeMsTodo,
  reopenTask as reopenMsTodo,
  createTask as createMsTodo, todoDueDate } from '../../utils/microsoftTodoService';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import {
  WERKDAG, fmtDuur, fmtUur, snapKwartier, taakMinuten, uurVan, vrijeMinuten,
  planBlokken, isAchterstallig, isVoorbijeDagstaat, einddatum, ordenen, restVanDag, teVerschuiven, groepeerOpProject, korteTitel,
  volgendeWerkdag, volgendeWerkdagNaam,
  duurDoel, ruweUren, metUren, splitsUitkomsten, bulkMelding, isInternProject, aantalInterneDeadlines,
  projectUrlVoorDeadline,
  naarMorgenKandidaten,
} from './dagplanning';
import { KLEUR, MAAT, RijInhoud, SorteerbareRij, SleepbareRij, ProjectTag, korteDatum } from './DagTaakRij';
import DagTijdlijn, { TL_VAN, TL_TOT, TL_PXH } from './DagTijdlijn';
import Achterstallig from './Achterstallig';
import OverlegTaak from './OverlegTaak';
import { NietGehouden } from '../Overleg/Verzetten';
import { usePlanOpslag } from './planOpslag';

// ─── Constants ───────────────────────────────────────────────────────────────

// PrioNr values that are calendar/agenda items, not actionable tasks
const SKIP_PRIO_BACKLOG = NIET_PRODUCTIE_SET;

// For today/tomorrow columns: show agenda items but hide Vanallesuurtje
const HIDE_FROM_DAY = new Set(['Vanallesuurtje']);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayStr() {
  return formatDateISO(new Date());
}

// "Morgen" = de volgende werkdag: op vrijdag (en in het weekend) is dat maandag,
// net als in Weekplan. Alle morgen-acties, de drop-zone en het tabblad volgen dit.
function tomorrowStr() {
  return formatDateISO(volgendeWerkdag(new Date()));
}
const morgenNaam = () => volgendeWerkdagNaam(new Date()); // "morgen" of "maandag"
const MorgenHoofd = () => { const n = morgenNaam(); return n.charAt(0).toUpperCase() + n.slice(1); };

function longDateFor(date) {
  const days = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
}

function tomorrowLongDate() {
  return longDateFor(volgendeWerkdag(new Date()));
}

const formatDate = (d) => korteDatum(d);
const longDate = () => longDateFor(new Date());

function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const target = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

const nuAlsUur = () => { const d = new Date(); return d.getHours() + d.getMinutes() / 60; };

// Hoort een Airtable-afspraak (met Outlook-begintijd) bij dit Outlook-event?
const normTitel = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
function zelfdeAfspraak(taak, ev) {
  const a = normTitel(taak.title); const b = normTitel(ev.subject);
  if (!a || !b) return false;
  if (a === b) return true;
  const evHM = String(ev.start || '').slice(11, 16);
  const taakHM = taak.startTime
    ? new Date(taak.startTime).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
    : null;
  return taakHM === evHM && (a.includes(b) || b.includes(a));
}

// Normalize task from various sources
function mapAirtableTask(rec) {
  const f = rec.fields || {};
  return {
    id: `at-${rec.id}`,
    source: 'airtable',
    title: f.NaamTaak || 'Naamloos',
    isComplete: f.PrioNr === 'Afgerond' || f.PrioNr === 'Opgestuurd' || !!f.Afgerond,
    priority: f.PrioNr || 'Geen prio',
    type: f.Type || null,
    hours: parseFloat(f.UrenWerk) || null,
    // Begintijd uit de gekoppelde Outlook-afspraak (lookup, kan array zijn)
    startTime: (Array.isArray(f['Begintijd (from MeetingsOutlook)'])
      ? f['Begintijd (from MeetingsOutlook)'][0]
      : f['Begintijd (from MeetingsOutlook)']) || null,
    deadline: f.Deadline || f.EndDateUitvoerder || null,
    projectName: f['Prnr en Naam']?.[0] || f.ProjectNummer?.[0] || null,
    projectId: f.Project?.[0] || null,
    startDate: f.StartDateUitvoerder || null,
    notes: f.Opmerking || f.Opmerkingen || null,
    _airtableId: rec.id,
    _raw: rec,
  };
}

// Vorige planning vastleggen vóór een verplaatsing, zodat "Ongedaan maken" exact terug kan.
// item.startDate is leidend (kan al optimistic verschoven zijn); de einddatum komt uit het
// ruwe record zolang dat nog bij die startdatum hoort.
const vorigeDatums = (item) => {
  const f = item?._raw?.fields || {};
  const start = item?.startDate || f.StartDateUitvoerder || null;
  const eind = f.StartDateUitvoerder === start ? (f.EndDateUitvoerder || start) : start;
  return { StartDateUitvoerder: start, EndDateUitvoerder: eind };
};

function mapMsTodo(todo, listId) {
  return {
    id: `ms-${todo.id}`,
    source: 'mstodo',
    title: todo.title || 'Taak',
    isComplete: todo.status === 'completed',
    priority: todo.importance === 'high' ? 'Prio 1' : todo.importance === 'low' ? 'Prio 4' : null,
    type: null,
    hours: null,
    deadline: todoDueDate(todo),
    projectName: null,
    projectId: null,
    startDate: null,
    notes: todo.body?.content || null,
    _msListId: listId,
    _msTodoId: todo.id,
    _raw: todo,
  };
}

function mapActionItem(item) {
  return {
    id: `act-${item.id}`,
    source: 'action',
    title: item.text,
    isComplete: false,
    priority: null,
    type: null,
    hours: null,
    deadline: null,
    projectName: item.projectName || null,
    projectId: item.projectId || null,
    startDate: null,
    notes: null,
    _noteId: item.noteId,
    _noteDate: item.noteDate,
    _raw: item,
  };
}

// Een PATCH die een fout ook echt als fout meldt (authFetch gooit niet bij 4xx/5xx).
// Via schrijfTaak: Rooster/Capaciteit/Dagstart tonen daarna niet meer de oude stand.
async function patchTaak(airtableId, velden) {
  const r = await schrijfTaak(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(velden),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r;
}

// ─── Kleine bouwstenen ───────────────────────────────────────────────────────

function DropZone({ id, children, minHeight = 80 }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Box ref={setNodeRef} sx={{
      minHeight, borderRadius: '12px',
      outline: isOver ? `1px dashed ${KLEUR.primair}` : '1px dashed transparent',
      outlineOffset: 2,
      backgroundColor: 'transparent',
      transition: 'all 0.15s ease',
    }}>
      {children}
    </Box>
  );
}

// Tablabel dat ook een drop-doel is (sleep een taak op "Morgen").
function DropTabLabel({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Box ref={setNodeRef} sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
      outline: isOver ? `1px dashed ${KLEUR.primair}` : 'none', outlineOffset: 2,
    }}>
      {children}
    </Box>
  );
}

function Teller({ n }) {
  if (!n) return null;
  return (
    <Box component="span" sx={{ fontSize: MAAT.klein, color: KLEUR.zacht }}>
      {n > 99 ? '99+' : n}
    </Box>
  );
}

// Compacte, neutrale sectie onder de dagkolom (opleveringen, PL-todo's, overleg).
function Sectie({ titel, aantal, standaardOpen = false, rechts, children }) {
  const [open, setOpen] = useState(standaardOpen);
  return (
    <Box sx={{ mt: 0.25 }}>
      <Box
        role="button" tabIndex={0} aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, px: 0.5, cursor: 'pointer', borderRadius: '8px', '&:hover': { backgroundColor: '#f7f8fa' } }}
      >
        <Typography sx={{ fontSize: MAAT.tekst, color: KLEUR.zacht }}>{titel}{aantal != null ? ` (${aantal})` : ''}</Typography>
        {open ? <ExpandMoreIcon sx={{ fontSize: 16, color: KLEUR.stil }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: KLEUR.stil }} />}
        <Box sx={{ flex: 1 }} />
        {open && rechts}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 1.5, pr: 0.5, pb: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

function SectieRegel({ titel, sub, rechts, onClick }) {
  return (
    <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, cursor: onClick ? 'pointer' : 'default', borderRadius: '6px', '&:hover': onClick ? { backgroundColor: KLEUR.achter } : {} }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: MAAT.tekst, color: KLEUR.tekst, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{titel}</Typography>
        {sub && <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</Typography>}
      </Box>
      {rechts}
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Vandaag() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userEmail = activeAccount?.username || '';
  const userName = activeAccount?.name || '';

  // State
  const [todayTasks, setTodayTasks] = useState([]);
  const [tomorrowTasks, setTomorrowTasks] = useState([]);
  const [backlogTasks, setBacklogTasks] = useState([]);
  const [msTodos, setMsTodos] = useState([]);
  const [msListId, setMsListId] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState({ tasks: true, mstodo: true, actions: true });
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [quickAddText, setQuickAddText] = useState('');
  const [dragActive, setDragActive] = useState(null);
  const [todayDeadlines, setTodayDeadlines] = useState([]);
  const [overdueDeadlines, setOverdueDeadlines] = useState([]);
  const [showInternalDeadlines, setShowInternalDeadlines] = useState(false);
  const [dlAnchor, setDlAnchor] = useState(null);
  const [selectedDl, setSelectedDl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null); // taak in het TaakPaneel
  const [paneelFocus, setPaneelFocus] = useState(null); // 'feedback' als een PL-todo het paneel opende
  const [datePickerAnchor, setDatePickerAnchor] = useState(null); // datepicker popover
  const [datePickerItem, setDatePickerItem] = useState(null);
  const [datePickerValue, setDatePickerValue] = useState('');
  const [userRecordId, setUserRecordId] = useState(null);
  const [backlogQuickAdd, setBacklogQuickAdd] = useState('');
  const [todoQuickAdd, setTodoQuickAdd] = useState('');
  const pendingCreatesRef = useRef(new Map());
  const navigate = useNavigate();

  // Fetch the user's Personen record ID — required as Assignee on new Airtable tasks,
  // otherwise fetchTasks (which filters by Email (Assignee)) won't return them.
  useEffect(() => {
    if (!userEmail) return;
    authFetch(`${BACKEND_URL}/Api/personen?email=${encodeURIComponent(userEmail)}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        const person = (data.personen || []).find(p =>
          p.fields?.Email === userEmail || p.fields?.['Email (from Account)']?.[0] === userEmail
        );
        if (person) setUserRecordId(person.id);
      })
      .catch(() => toast.error('Je persoonsrecord kon niet worden geladen — nieuwe taken krijgen geen uitvoerder'));
  }, [userEmail]);

  // PL-todo's: wat jij als projectleider vandaag nog moet regelen (feedback in v2/DEF-taken).
  const [plTodos, setPlTodos] = useState([]);
  // Alleen het antwoord van de laatste aanvraag telt (opslaan → ongedaan maken kort na elkaar:
  // een traag leeg antwoord mag de teruggezette todo niet alsnog wegpoetsen).
  const plTodosVersie = useRef(0);
  const laadPlTodos = useCallback(async () => {
    if (!userEmail) return;
    const versie = ++plTodosVersie.current;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/pl-todos`);
      if (!r.ok) return;
      const d = await r.json();
      if (versie === plTodosVersie.current) setPlTodos(d.todos || []);
    } catch { /* geen todo's is geen fout in beeld waard */ }
  }, [userEmail]);
  useEffect(() => { laadPlTodos(); }, [laadPlTodos]);

  // Een PL-todo ("zet de feedback in de taak") opent die taak in het TaakPaneel, met de cursor
  // in het feedbackveld. De taak is meestal van een editor en staat dus niet in jouw lijsten:
  // het paneel haalt de rest vers op. Geen `_airtableId`, zodat naPaneelWijziging (dat op je
  // eigen lijsten werkt) dit losse object niet omzet.
  // Eerst de actuele feedback ophalen: iemand kan hem intussen hebben ingevuld, en een leeg
  // veld opslaan zou die dan wissen. Lukt ophalen niet, dan niet openen (geen blind bewerken).
  // plKlik: elke andere taakselectie of sluiten maakt een lopende opening ongeldig.
  const plKlik = useRef(0);
  const openPlTodo = useCallback(async (t) => {
    if (!t?.taskId) { navigate(t?.link || '/dagstart'); return; }
    const klik = ++plKlik.current;
    let fb;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/taak-feedback?ids=${encodeURIComponent(t.taskId)}`);
      fb = (await leesJson(r, 'Feedback laden'))?.[t.taskId] || {};
    } catch (err) {
      if (klik === plKlik.current) meldFout(err, 'Feedback laden');
      return;
    }
    if (klik !== plKlik.current) return; // intussen een andere todo aangeklikt
    const heeft = Boolean(fb.feedbackLink || fb.feedbackTekst || fb.feedbackPunten > 0);
    setSelectedTask({
      id: t.taskId,
      name: t.taskName || '',
      projectName: [t.projectNumber, t.projectName].filter(Boolean).join(' ') || undefined,
      projectNumber: t.projectNumber || undefined,
      feedbackVereist: true,
      feedbackLink: fb.feedbackLink || null,
      feedbackTekst: fb.feedbackTekst || null,
      feedbackPunten: fb.feedbackPunten || 0,
      feedbackOntbreekt: !heeft,
      _plTodo: true,
    });
    setPaneelFocus('feedback');
    if (heeft) laadPlTodos(); // was al ingevuld: todo is achterhaald
  }, [navigate, laadPlTodos]);

  // Feedback opgeslagen (of ongedaan gemaakt) in het paneel: taak bijwerken en de PL-todo's
  // opnieuw ophalen — een gevulde taak valt dan uit de lijst.
  const naFeedbackLokaal = useCallback((taakId, velden) => {
    setSelectedTask((s) => (s && s.id === taakId ? { ...s, ...velden } : s));
    laadPlTodos();
  }, [laadPlTodos]);

  // Overlegmodule: mijn overleggen deze week + open acties (compact). 403 = geen recht → blok blijft weg.
  const [overleg, setOverleg] = useState(null);
  const laadOverleg = useCallback(async () => {
    if (!userEmail) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overleg/mijn-week?compact=1`);
      if (!r.ok) return;
      const d = await r.json();
      const iets = (d.sessies || []).length || (d.todos || []).length || (d.in_te_vullen || []).length || (d.niet_gehouden || []).length;
      setOverleg(iets ? d : null);
    } catch { /* stil: geen overleggen is geen fout */ }
  }, [userEmail]);
  useEffect(() => { laadOverleg(); }, [laadOverleg]);

  // ─── Data Fetching ───────────────────────────────────────────────────────

  const fetchTasks = useCallback(async () => {
    if (!userEmail) return;
    setLoading(prev => ({ ...prev, tasks: true }));
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`);
      const data = await leesJson(resp, 'Taken laden');
      const tasks = (data.tasks || []).map(mapAirtableTask);
      const today = todayStr();
      const tomorrow = tomorrowStr();
      setTodayTasks(tasks.filter(t => t.startDate === today && !HIDE_FROM_DAY.has(t.priority)));
      setTomorrowTasks(tasks.filter(t => t.startDate === tomorrow && !t.isComplete && !HIDE_FROM_DAY.has(t.priority)));
      // Een voorbije draaidag of vrije dag is geen backlogwerk (Joris, 3 nov 2026); een die nog komt wel.
      setBacklogTasks(tasks.filter(t => t.startDate !== today && t.startDate !== tomorrow && !t.isComplete && !SKIP_PRIO_BACKLOG.has(t.priority) && !isVoorbijeDagstaat(t, today)));
    } catch (err) {
      console.error('[Vandaag] Failed to fetch tasks:', err);
      toast.error('Taken laden mislukt');
    } finally {
      setLoading(prev => ({ ...prev, tasks: false }));
    }
  }, [userEmail]);

  const fetchMsTodos = useCallback(async () => {
    if (!userEmail) return;
    const account = instance.getActiveAccount();
    if (!account) return;
    setLoading(prev => ({ ...prev, mstodo: true }));
    try {
      initMicrosoftTodoService(instance, account);
      const result = await getMyTodos(false);
      // Deduplicate by task ID (MS Graph can occasionally return duplicates)
      const seen = new Set();
      const uniqueTasks = (result.tasks || []).filter(t => {
        if (seen.has(t.id)) return false;
        seen.add(t.id);
        return true;
      });
      setMsTodos(uniqueTasks.map(t => mapMsTodo(t, result.listId)));
      setMsListId(result.listId);
    } catch (err) {
      console.error('[Vandaag] MS To-Do fetch failed:', err);
      // Not critical - just show empty
    } finally {
      setLoading(prev => ({ ...prev, mstodo: false }));
    }
  }, [userEmail, instance]);

  const fetchActionItems = useCallback(async () => {
    if (!userEmail) return;
    setLoading(prev => ({ ...prev, actions: true }));
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/my-action-items?email=${encodeURIComponent(userEmail)}`);
      const data = await leesJson(resp, 'Actiepunten laden');
      setActionItems((data.actionItems || []).map(mapActionItem));
    } catch (err) {
      console.error('[Vandaag] Action items fetch failed:', err);
      meldFout(err, 'Actiepunten laden');
    } finally {
      setLoading(prev => ({ ...prev, actions: false }));
    }
  }, [userEmail]);

  // Alleen het nieuwste antwoord telt (wijzigen → ongedaan maken kort na elkaar).
  const deadlinesVersie = useRef(0);
  const fetchDeadlines = useCallback(async () => {
    if (!userEmail) return;
    const versie = ++deadlinesVersie.current;
    try {
      const today = todayStr();
      // Fetch today + overdue (endDate=today, no startDate → includes all past items)
      const resp = await authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}&endDate=${today}`);
      const data = await leesJson(resp, 'Deadlines laden');
      const all = data.byDate || [];
      const doneStatuses = AFGEROND_SET;
      const todayItems = all.filter(d => d.deadline?.slice(0, 10) === today);
      const overdueItems = all.filter(d => {
        const dt = d.deadline?.slice(0, 10);
        return dt && dt < today && !doneStatuses.has(d.status);
      });
      if (versie !== deadlinesVersie.current) return;
      setTodayDeadlines(todayItems);
      setOverdueDeadlines(overdueItems);
    } catch (err) {
      if (versie !== deadlinesVersie.current) return;
      console.error('[Vandaag] Deadlines fetch failed:', err);
      meldFout(err, 'Deadlines laden');
    }
  }, [userEmail]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => { fetchMsTodos(); }, [fetchMsTodos]);
  useEffect(() => { fetchActionItems(); }, [fetchActionItems]);
  useEffect(() => { fetchDeadlines(); }, [fetchDeadlines]);

  // ─── Task Actions ────────────────────────────────────────────────────────

  // Resolve the Airtable record id for an item, awaiting a pending create if needed.
  // Why: quick-add inserts a temp task with _airtableId: null and POSTs in the background;
  // if the user moves/edits the task before the POST resolves, we'd otherwise lose the change.
  const resolveAirtableId = useCallback(async (item) => {
    if (item._airtableId) return item._airtableId;
    const pending = pendingCreatesRef.current.get(item.id);
    if (!pending) return null;
    try {
      const created = await pending;
      return created?.id || null;
    } catch {
      return null;
    }
  }, []);

  // Ongedaan maken van een verplaatsing: vorige datums terugzetten en de lijsten verversen.
  const herstelDatums = useCallback(async (airtableId, vorige) => {
    await schrijfTaak(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vorige),
    });
    await fetchTasks();
  }, [fetchTasks]);

  // Move a backlog task to today
  const moveToToday = useCallback(async (item) => {
    const today = todayStr();
    if (item.source === 'airtable' && item._airtableId) {
      const vorige = vorigeDatums(item);
      // Optimistic update
      setBacklogTasks(prev => prev.filter(t => t.id !== item.id));
      setTodayTasks(prev => [...prev, { ...item, startDate: today }]);
      try {
        await schrijfTaak(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: today, EndDateUitvoerder: today }),
        });
        biedOngedaanAan('Naar vandaag verplaatst', () => herstelDatums(item._airtableId, vorige));
      } catch (err) {
        // Revert
        setTodayTasks(prev => prev.filter(t => t.id !== item.id));
        setBacklogTasks(prev => [...prev, item]);
        toast.error('Verplaatsen mislukt');
      }
    } else if (item.source === 'mstodo') {
      // Create Airtable task from MS To-Do
      setMsTodos(prev => prev.filter(t => t.id !== item.id));
      const tempId = `temp-${Date.now()}`;
      const tempTask = { ...item, id: tempId, source: 'airtable', startDate: today };
      setTodayTasks(prev => [...prev, tempTask]);
      try {
        const body = {
          NaamTaak: item.title,
          StartDateUitvoerder: today,
          EndDateUitvoerder: today,
          PrioNr: 'Geen prio',
        };
        if (userRecordId) body.Assignee = [userRecordId];
        const resp = await schrijfTaak(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const created = await leesJson(resp, 'Taak aanmaken');
        // Replace temp with real task
        setTodayTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created.task || created) : t));
        toast.success('Taak aangemaakt vanuit To-Do');
      } catch (err) {
        setTodayTasks(prev => prev.filter(t => t.id !== tempId));
        setMsTodos(prev => [...prev, item]);
        toast.error('Aanmaken mislukt');
      }
    } else if (item.source === 'action') {
      // Create Airtable task from action item
      setActionItems(prev => prev.filter(t => t.id !== item.id));
      const tempId = `temp-${Date.now()}`;
      const tempTask = { ...item, id: tempId, source: 'airtable', startDate: today };
      setTodayTasks(prev => [...prev, tempTask]);
      try {
        const body = {
          NaamTaak: item.title,
          StartDateUitvoerder: today,
          EndDateUitvoerder: today,
          PrioNr: 'Geen prio',
        };
        if (item.projectId) body.Project = [item.projectId];
        if (userRecordId) body.Assignee = [userRecordId];
        const resp = await schrijfTaak(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const created = await leesJson(resp, 'Taak aanmaken');
        setTodayTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created.task || created) : t));
        toast.success('Taak aangemaakt vanuit actiepunt');
      } catch (err) {
        setTodayTasks(prev => prev.filter(t => t.id !== tempId));
        setActionItems(prev => [...prev, item]);
        toast.error('Aanmaken mislukt');
      }
    }
  }, [userRecordId, herstelDatums]);

  // Move a task to tomorrow
  const moveToTomorrow = useCallback(async (item) => {
    const tomorrow = tomorrowStr();
    const vorige = vorigeDatums(item);
    // Optimistic update
    setTodayTasks(prev => prev.filter(t => t.id !== item.id));
    setTomorrowTasks(prev => [...prev, { ...item, startDate: tomorrow }]);
    const airtableId = await resolveAirtableId(item);
    if (!airtableId) {
      setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
      setTodayTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
      return;
    }
    try {
      await schrijfTaak(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ StartDateUitvoerder: tomorrow, EndDateUitvoerder: tomorrow }),
      });
      biedOngedaanAan(`Naar ${morgenNaam()} verplaatst`, () => herstelDatums(airtableId, vorige));
    } catch (err) {
      setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
      setTodayTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
    }
  }, [resolveAirtableId, herstelDatums]);

  // Move a task from tomorrow back to today
  const moveFromTomorrowToToday = useCallback(async (item) => {
    const today = todayStr();
    const vorige = vorigeDatums(item);
    setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
    setTodayTasks(prev => [...prev, { ...item, startDate: today }]);
    const airtableId = await resolveAirtableId(item);
    if (!airtableId) {
      setTodayTasks(prev => prev.filter(t => t.id !== item.id));
      setTomorrowTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
      return;
    }
    try {
      await schrijfTaak(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ StartDateUitvoerder: today, EndDateUitvoerder: today }),
      });
      biedOngedaanAan('Naar vandaag verplaatst', () => herstelDatums(airtableId, vorige));
    } catch (err) {
      setTodayTasks(prev => prev.filter(t => t.id !== item.id));
      setTomorrowTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
    }
  }, [resolveAirtableId, herstelDatums]);

  // Move a task (or source item) to a specific date
  const moveToDate = useCallback(async (item, dateStr) => {
    if (!dateStr) return;
    const today = todayStr();
    const tomorrow = tomorrowStr();

    // Existing airtable task or freshly-created (still pending) task
    if (item._airtableId || pendingCreatesRef.current.has(item.id)) {
      const vorige = vorigeDatums(item);
      // Remove from current list
      setTodayTasks(prev => prev.filter(t => t.id !== item.id));
      setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
      setBacklogTasks(prev => prev.filter(t => t.id !== item.id));
      // Add to target list
      const updated = { ...item, startDate: dateStr };
      if (dateStr === today) setTodayTasks(prev => [...prev, updated]);
      else if (dateStr === tomorrow) setTomorrowTasks(prev => [...prev, updated]);
      // else it goes to backlog on next refresh
      const airtableId = await resolveAirtableId(item);
      if (!airtableId) {
        toast.error('Verplaatsen mislukt');
        fetchTasks();
        return;
      }
      try {
        await schrijfTaak(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: dateStr, EndDateUitvoerder: dateStr }),
        });
        biedOngedaanAan(`Verplaatst naar ${formatDate(dateStr)}`, () => herstelDatums(airtableId, vorige));
      } catch (err) {
        toast.error('Verplaatsen mislukt');
        fetchTasks(); // refresh to restore state
      }
    } else if (item.source === 'mstodo' || item.source === 'action') {
      // Create new airtable task for this date
      setMsTodos(prev => prev.filter(t => t.id !== item.id));
      setActionItems(prev => prev.filter(t => t.id !== item.id));
      const tempId = `temp-${Date.now()}`;
      const tempTask = { ...item, id: tempId, source: 'airtable', startDate: dateStr };
      if (dateStr === today) setTodayTasks(prev => [...prev, tempTask]);
      else if (dateStr === tomorrow) setTomorrowTasks(prev => [...prev, tempTask]);
      try {
        const body = { NaamTaak: item.title, StartDateUitvoerder: dateStr, EndDateUitvoerder: dateStr, PrioNr: 'Geen prio' };
        if (item.projectId) body.Project = [item.projectId];
        if (userRecordId) body.Assignee = [userRecordId];
        const resp = await schrijfTaak(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const created = await leesJson(resp, 'Taak aanmaken');
        const mapped = mapAirtableTask(created.task || created);
        setTodayTasks(prev => prev.map(t => t.id === tempId ? mapped : t));
        setTomorrowTasks(prev => prev.map(t => t.id === tempId ? mapped : t));
        toast.success(`Taak aangemaakt voor ${formatDate(dateStr)}`);
      } catch (err) {
        setTodayTasks(prev => prev.filter(t => t.id !== tempId));
        setTomorrowTasks(prev => prev.filter(t => t.id !== tempId));
        toast.error('Aanmaken mislukt');
      }
    }
  }, [fetchTasks, resolveAirtableId, userRecordId, herstelDatums]);

  // Na een wijziging in het TaakPaneel: het ruwe record bijwerken en opnieuw omzetten, zodat
  // de regel direct klopt. Verschuift de datum of de afgerond-stand, dan hoort de taak in
  // een andere kolom: dan de lijsten opnieuw ophalen.
  const naPaneelWijziging = useCallback((airtableId, velden) => {
    const werkBij = (prev) => prev.map((t) => {
      if (t._airtableId !== airtableId) return t;
      const raw = { ...(t._raw || { id: airtableId }), fields: { ...(t._raw?.fields || {}), ...velden } };
      return { ...mapAirtableTask(raw), id: t.id };
    });
    setTodayTasks(werkBij);
    setTomorrowTasks(werkBij);
    setBacklogTasks(werkBij);
    setSelectedTask((s) => (s && s._airtableId === airtableId ? werkBij([s])[0] : s));
    if (['StartDateUitvoerder', 'EndDateUitvoerder', 'Afgerond'].some((k) => k in velden)) fetchTasks();
  }, [fetchTasks]);

  // Mark a completed task as "Opgestuurd"
  const markAsSent = useCallback(async (item) => {
    if (!item._airtableId) return;
    // Optimistic update
    setTodayTasks(prev => prev.map(t =>
      t.id === item.id ? { ...t, priority: 'Opgestuurd' } : t
    ));
    setTomorrowTasks(prev => prev.map(t =>
      t.id === item.id ? { ...t, priority: 'Opgestuurd' } : t
    ));
    try {
      await schrijfTaak(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PrioNr: 'Opgestuurd', Afgerond: true }),
      });
      // Terug naar de vorige status (meestal "Afgerond") — misklik op het kleine knopje.
      const terugPrio = item.priority || 'Afgerond';
      const zetTerug = (prev) => prev.map(t => (t.id === item.id ? { ...t, priority: terugPrio } : t));
      biedOngedaanAan('Gemarkeerd als Opgestuurd', async () => {
        await schrijfTaak(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ PrioNr: terugPrio, Afgerond: !!item.isComplete }),
        });
        setTodayTasks(zetTerug);
        setTomorrowTasks(zetTerug);
      });
    } catch (err) {
      // Revert
      setTodayTasks(prev => prev.map(t =>
        t.id === item.id ? { ...t, priority: item.priority } : t
      ));
      setTomorrowTasks(prev => prev.map(t =>
        t.id === item.id ? { ...t, priority: item.priority } : t
      ));
      toast.error('Status wijzigen mislukt');
    }
  }, []);

  // Toggle task completion
  const toggleComplete = useCallback(async (item) => {
    const newComplete = !item.isComplete;
    const zetBeide = (fn) => { setTodayTasks(fn); setTomorrowTasks(fn); };
    // Optimistic
    zetBeide(prev => prev.map(t =>
      t.id === item.id ? { ...t, isComplete: newComplete, priority: newComplete ? 'Afgerond' : 'Geen prio' } : t
    ));
    try {
      if (item._airtableId) {
        await schrijfTaak(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            PrioNr: newComplete ? 'Afgerond' : 'Geen prio',
            Afgerond: newComplete,
          }),
        });
      }
      // Sync back to MS To-Do if applicable
      if (newComplete && item._msTodoId && item._msListId) {
        try { await completeMsTodo(item._msListId, item._msTodoId); } catch { /* silent */ }
      }
      if (newComplete) {
        const terugPrio = item.priority && !AFGEROND_SET.has(item.priority) ? item.priority : 'Geen prio';
        biedOngedaanAan(`"${item.title}" afgerond`, async () => {
          if (item._airtableId) {
            await schrijfTaak(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ PrioNr: terugPrio, Afgerond: false }),
            });
          }
          if (item._msTodoId && item._msListId) {
            try { await reopenMsTodo(item._msListId, item._msTodoId); } catch { /* To Do heropenen is best effort */ }
          }
          zetBeide(prev => prev.map(t =>
            t.id === item.id ? { ...t, isComplete: false, priority: terugPrio } : t
          ));
        });
      }
    } catch (err) {
      // Revert
      zetBeide(prev => prev.map(t =>
        t.id === item.id ? { ...t, isComplete: !newComplete, priority: item.priority } : t
      ));
      toast.error('Status bijwerken mislukt');
    }
  }, []);

  // Quick add task
  const quickAdd = useCallback(async () => {
    const text = quickAddText.trim();
    if (!text) return;
    if (!userRecordId) {
      toast.error('Profiel nog niet geladen — probeer opnieuw');
      return;
    }
    setQuickAddText('');
    const today = todayStr();
    const tempId = `temp-${Date.now()}`;
    setTodayTasks(prev => [...prev, {
      id: tempId, source: 'airtable', title: text, isComplete: false,
      priority: 'Geen prio', type: null, hours: null, deadline: null,
      projectName: null, projectId: null, startDate: today, _airtableId: null,
    }]);
    const createPromise = (async () => {
      const resp = await schrijfTaak(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          NaamTaak: text,
          StartDateUitvoerder: today,
          EndDateUitvoerder: today,
          PrioNr: 'Geen prio',
          Assignee: [userRecordId],
        }),
      });
      if (!resp.ok) throw new Error('create failed');
      const data = await resp.json();
      return data.task || data;
    })();
    pendingCreatesRef.current.set(tempId, createPromise);
    try {
      const created = await createPromise;
      setTodayTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created) : t));
    } catch (err) {
      setTodayTasks(prev => prev.filter(t => t.id !== tempId));
      toast.error('Taak aanmaken mislukt');
    } finally {
      pendingCreatesRef.current.delete(tempId);
    }
  }, [quickAddText, userRecordId]);

  // Quick add directly to backlog (no start date — lands in backlog after refresh and immediately)
  const quickAddBacklog = useCallback(async () => {
    const text = backlogQuickAdd.trim();
    if (!text) return;
    if (!userRecordId) {
      toast.error('Profiel nog niet geladen — probeer opnieuw');
      return;
    }
    setBacklogQuickAdd('');
    const tempId = `temp-bl-${Date.now()}`;
    setBacklogTasks(prev => [...prev, {
      id: tempId, source: 'airtable', title: text, isComplete: false,
      priority: 'Geen prio', type: null, hours: null, deadline: null,
      projectName: null, projectId: null, startDate: null, _airtableId: null,
    }]);
    try {
      const resp = await schrijfTaak(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          NaamTaak: text,
          PrioNr: 'Geen prio',
          Assignee: [userRecordId],
        }),
      });
      if (!resp.ok) throw new Error('create failed');
      const data = await resp.json();
      const created = data.task || data;
      setBacklogTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created) : t));
      toast.success('Taak toegevoegd aan backlog');
    } catch (err) {
      setBacklogTasks(prev => prev.filter(t => t.id !== tempId));
      toast.error('Taak aanmaken mislukt');
    }
  }, [backlogQuickAdd, userRecordId]);

  // Quick add Microsoft To-Do task
  const quickAddTodo = useCallback(async () => {
    const text = todoQuickAdd.trim();
    if (!text) return;
    if (!msListId) {
      toast.error('Microsoft To-Do lijst niet beschikbaar');
      return;
    }
    setTodoQuickAdd('');
    try {
      const created = await createMsTodo(msListId, { title: text });
      setMsTodos(prev => [...prev, mapMsTodo(created, msListId)]);
      toast.success('To-Do toegevoegd');
    } catch (err) {
      console.error('[Vandaag] MS To-Do create failed:', err);
      toast.error('To-Do aanmaken mislukt');
    }
  }, [todoQuickAdd, msListId]);

  // ─── Dagplan (volgorde, duur, tijdblokken) ───────────────────────────────

  const vandaag = todayStr();
  const [plan, bewaarPlan] = usePlanOpslag(userEmail, vandaag);
  const [nu, setNu] = useState(nuAlsUur);
  useEffect(() => {
    const t = setInterval(() => setNu(nuAlsUur()), 60000);
    return () => clearInterval(t);
  }, []);

  // Outlook-agenda van vandaag en morgen (dezelfde bron als Weekplan).
  const [calEvents, setCalEvents] = useState({ today: [], tomorrow: [] });
  const [agendaFout, setAgendaFout] = useState(false);
  const laadAgenda = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([fetchDayEvents(todayStr()), fetchDayEvents(tomorrowStr())]);
      setCalEvents({ today: Array.isArray(t) ? t : [], tomorrow: Array.isArray(m) ? m : [] });
      setAgendaFout(false);
    } catch (e) {
      console.warn('[Vandaag] agenda ophalen mislukt:', e.message);
      setAgendaFout(true);
    }
  }, []);
  useEffect(() => { laadAgenda(); }, [laadAgenda]);

  const refreshAll = () => { fetchTasks(); fetchMsTodos(); fetchActionItems(); fetchDeadlines(); laadPlTodos(); laadAgenda(); };

  // Taken met een Outlook-begintijd zijn afspraken → tijdlijn; de rest is het dagplan.
  const agendaTaken = useMemo(() => todayTasks.filter((t) => !t.isComplete && t.startTime), [todayTasks]);
  const dagTaken = useMemo(
    () => ordenen(todayTasks.filter((t) => !t.isComplete && !t.startTime), plan.volgorde),
    [todayTasks, plan.volgorde],
  );
  const afgerondVandaag = useMemo(() => todayTasks.filter((t) => t.isComplete), [todayTasks]);
  const minutenVan = useCallback((t) => taakMinuten(t, plan.duur), [plan.duur]);
  const geplandMin = dagTaken.reduce((som, t) => som + minutenVan(t).min, 0);

  const afspraken = useMemo(() => {
    const uitOutlook = calEvents.today.map((ev) => {
      const s = uurVan(ev.start);
      const e = uurVan(ev.end);
      return {
        id: `ol-${ev.id}`, titel: ev.subject, s, e: e != null && e > s ? e : s + 0.5,
        vrij: ev.showAs === 'free', item: agendaTaken.find((t) => zelfdeAfspraak(t, ev)) || null,
      };
    }).filter((a) => a.s != null);
    const losseTaken = agendaTaken
      .filter((t) => !calEvents.today.some((ev) => zelfdeAfspraak(t, ev)))
      .map((t) => {
        const s = uurVan(t.startTime);
        return { id: t.id, titel: t.title, s, e: s + (t.hours || 1), vrij: false, item: t };
      })
      .filter((a) => a.s != null);
    return [...uitOutlook, ...losseTaken];
  }, [calEvents.today, agendaTaken]);

  // Capaciteit telt van nu tot einde werkdag (vóór de start: de hele dag).
  const rest = restVanDag(nu, WERKDAG);
  const vrijMin = rest.eind > rest.start ? vrijeMinuten(afspraken, rest) : 0;
  const afspraakMin = Math.round((rest.eind - rest.start) * 60) - vrijMin;
  const aantalAfspraken = afspraken.filter((a) => !a.vrij && a.e > rest.start && a.s < rest.eind).length;

  const blokken = useMemo(() => {
    const vanaf = nu > WERKDAG.start ? Math.ceil(nu * 4) / 4 : WERKDAG.start;
    const perId = new Map(dagTaken.map((t) => [t.id, t]));
    return planBlokken(
      dagTaken.map((t) => ({ id: t.id, min: minutenVan(t).min, vast: plan.tijden[t.id] ?? null })),
      afspraken,
      { vanaf, eind: WERKDAG.eind },
    ).map((b) => ({ ...b, item: perId.get(b.id) }));
  }, [dagTaken, minutenVan, plan.tijden, afspraken, nu]);
  const blokVan = useMemo(() => new Map(blokken.map((b) => [b.id, b])), [blokken]);

  const zetDuur = useCallback(async (item, min) => {
    // Ééndaagse taak → de uren in de taak zelf; meerdaagse (uit start/eind, ook zonder
    // uren) → alleen jouw schatting voor vandaag, anders werd "2 uur vandaag" het totaal.
    if (duurDoel(item) === 'taak') {
      const vorig = ruweUren(item); // leeg blijft leeg, 0 blijft 0
      const nieuw = Math.round((min / 60) * 100) / 100;
      const zet = (waarde) => setTodayTasks((prev) => prev.map((t) => (t.id === item.id ? metUren(t, waarde) : t)));
      bewaarPlan((p) => { const duur = { ...p.duur }; delete duur[item.id]; return { ...p, duur }; });
      zet(nieuw);
      try {
        await patchTaak(item._airtableId, { UrenWerk: nieuw });
        biedOngedaanAan(`Duur ${fmtDuur(min)}`, async () => {
          await patchTaak(item._airtableId, { UrenWerk: vorig });
          zet(vorig);
        });
      } catch {
        zet(vorig);
        toast.error('Duur opslaan mislukt');
      }
    } else {
      bewaarPlan((p) => ({ ...p, duur: { ...p.duur, [item.id]: min } }));
    }
  }, [bewaarPlan]);

  // Meerdere taken tegelijk naar morgen (vanuit "te veel gepland"), met één undo.
  // Meerdaagse taken doen niet mee (naarMorgenKandidaten): start én eind op morgen zou
  // het hele bereik op één dag zetten. Elke PATCH apart afwachten (allSettled): wat
  // lukte is terug te zetten, wat faalde staat weer op vandaag, en pas daarna herladen.
  const schuifNaarMorgen = useCallback(async (items) => {
    const morgen = tomorrowStr();
    const lijst = naarMorgenKandidaten(items.filter((i) => i._airtableId));
    if (!lijst.length) return;
    const ids = new Set(lijst.map((i) => i.id));
    setTodayTasks((prev) => prev.filter((t) => !ids.has(t.id)));
    setTomorrowTasks((prev) => [...prev, ...lijst.map((i) => ({ ...i, startDate: morgen }))]);
    bewaarPlan((p) => { const tijden = { ...p.tijden }; lijst.forEach((i) => delete tijden[i.id]); return { ...p, tijden }; });
    const uitkomsten = await Promise.allSettled(
      lijst.map((i) => patchTaak(i._airtableId, { StartDateUitvoerder: morgen, EndDateUitvoerder: morgen })));
    const { gelukt, mislukt } = splitsUitkomsten(lijst, uitkomsten);
    const melding = bulkMelding(gelukt.length, lijst.length, morgenNaam());
    if (gelukt.length) {
      biedOngedaanAan(melding, async () => {
        const terug = await Promise.allSettled(gelukt.map((i) => patchTaak(i._airtableId, vorigeDatums(i))));
        await fetchTasks();
        const niet = splitsUitkomsten(gelukt, terug).mislukt.length;
        if (niet) throw new Error(`${niet} van ${gelukt.length} niet teruggezet`);
      });
    }
    if (mislukt.length) {
      const terug = new Set(mislukt.map((i) => i.id));
      setTomorrowTasks((prev) => prev.filter((t) => !terug.has(t.id)));
      setTodayTasks((prev) => [...prev, ...mislukt]);
      toast.error(gelukt.length ? `${mislukt.length} niet verplaatst` : melding);
      fetchTasks();
    }
  }, [bewaarPlan, fetchTasks]);

  const zetTijd = useCallback((id, uur) => {
    bewaarPlan((p) => {
      const tijden = { ...p.tijden };
      if (uur == null) delete tijden[id]; else tijden[id] = uur;
      return { ...p, tijden };
    });
  }, [bewaarPlan]);

  // ─── Achterstallig: eigen taken over de einddatum + PL-deadlines ────────

  const [achterstalligOpen, setAchterstalligOpen] = useState(false);
  const [paneelOpen, setPaneelOpen] = useState(false);
  const achterstalligRef = useRef(null);

  const eigenAchterstallig = useMemo(
    () => backlogTasks.filter((t) => isAchterstallig(t, vandaag) && !t.title.toLowerCase().startsWith('reistijd')),
    [backlogTasks, vandaag],
  );
  const achterstalligRijen = useMemo(() => {
    const eigen = eigenAchterstallig.map((t) => ({
      key: t.id, soort: 'eigen', titel: t.title, projectName: t.projectName, datum: einddatum(t), taak: t,
    }));
    const eigenIds = new Set(eigenAchterstallig.map((t) => t._airtableId));
    const pl = overdueDeadlines
      .filter((d) => (showInternalDeadlines || !isInternProject(d.projectName)))
      .filter((d) => !(d.type === 'task' && !d.deliverableId))
      .filter((d) => !eigenIds.has(d.id))
      .map((d) => ({
        key: `dl-${d.id}`, soort: d.type === 'deliverable' ? 'deliverable' : 'pl',
        titel: d.name, projectName: d.projectName || null, datum: d.deadline?.slice(0, 10),
        wie: d.assignee || null, dl: d,
      }));
    return [...eigen, ...pl].sort((a, b) => String(a.datum).localeCompare(String(b.datum)));
  }, [eigenAchterstallig, overdueDeadlines, showInternalDeadlines]);
  // Schakelaar voor interne deadlines telt vandaag + achterstallig samen: anders was een
  // interne deadline van vandaag onbereikbaar zolang er geen interne achterstallige was.
  const interneDeadlines = aantalInterneDeadlines(todayDeadlines, overdueDeadlines);

  const rondEigenAf = useCallback(async (taak) => {
    setBacklogTasks((prev) => prev.filter((t) => t.id !== taak.id));
    try {
      await patchTaak(taak._airtableId, { PrioNr: 'Afgerond', Afgerond: true });
      const terug = taak.priority && !AFGEROND_SET.has(taak.priority) ? taak.priority : 'Geen prio';
      biedOngedaanAan(`"${taak.title}" afgerond`, async () => {
        await patchTaak(taak._airtableId, { PrioNr: terug, Afgerond: false });
        await fetchTasks();
      });
    } catch {
      setBacklogTasks((prev) => [...prev, taak]);
      toast.error('Afronden mislukt');
    }
  }, [fetchTasks]);

  const verzetPlDeadline = useCallback(async (rij, datum) => {
    const d = rij.dl;
    const vorige = { StartDateUitvoerder: d.startDate || null, EndDateUitvoerder: d.endDate || null };
    const velden = { EndDateUitvoerder: datum };
    if (d.startDate && d.startDate > datum) velden.StartDateUitvoerder = datum;
    setOverdueDeadlines((prev) => prev.filter((x) => x.id !== d.id));
    try {
      await patchTaak(d.id, velden);
      biedOngedaanAan(`"${d.name}" verzet naar ${formatDate(datum)}`, async () => {
        await patchTaak(d.id, vorige);
        await fetchDeadlines();
      });
      if (datum === todayStr()) fetchDeadlines();
    } catch {
      setOverdueDeadlines((prev) => [...prev, d]);
      toast.error('Verzetten mislukt');
    }
  }, [fetchDeadlines]);

  const rondPlAf = useCallback(async (rij) => {
    const d = rij.dl;
    setOverdueDeadlines((prev) => prev.filter((x) => x.id !== d.id));
    try {
      await patchTaak(d.id, { PrioNr: 'Afgerond', Afgerond: true });
      const terug = d.status && !AFGEROND_SET.has(d.status) ? d.status : 'Geen prio';
      biedOngedaanAan(`"${d.name}" afgerond`, async () => {
        await patchTaak(d.id, { PrioNr: terug, Afgerond: false });
        await fetchDeadlines();
      });
    } catch {
      setOverdueDeadlines((prev) => [...prev, d]);
      toast.error('Afronden mislukt');
    }
  }, [fetchDeadlines]);

  // ─── Taakpaneel en datumpopover ──────────────────────────────────────────

  const openDatePicker = useCallback((item, anchorEl) => {
    setDatePickerItem(item);
    setDatePickerAnchor(anchorEl);
    setDatePickerValue(item.startDate || tomorrowStr());
  }, []);

  // Een Airtable-taak opent het TaakPaneel; een To Do- of actiepunt heeft (nog) geen
  // taakrecord — daar was de enige zinnige actie "op een dag zetten", dus de datumkiezer.
  const handleClickTask = useCallback((item, event) => {
    if (item?._airtableId) { plKlik.current += 1; setPaneelFocus(null); setSelectedTask(item); return; }
    if (event?.currentTarget) openDatePicker(item, event.currentTarget);
  }, [openDatePicker]);

  // Een deadline die een taak is (PL-deadline, ook van een collega) opent net als je eigen
  // taken het TaakPaneel — met ongedaan maken. Deliverables/opleveringen zijn geen taak:
  // die houden het venstertje met "Ga naar project".
  const openDeadline = useCallback((d, event) => {
    if (d?.type === 'task' && d.id) {
      plKlik.current += 1; // een lopende PL-todo-opening mag deze taak niet meer vervangen
      setPaneelFocus(null);
      setSelectedTask({
        id: d.id, name: d.name || '', projectName: d.projectName || undefined,
        projectId: d.projectId || undefined, _bron: 'deadline',
      });
      return;
    }
    setDlAnchor(event?.currentTarget || null);
    setSelectedDl(d);
  }, []);

  // Eén ingang voor "verplaats naar datum": PL-deadlines verzetten alleen hun einddatum.
  const verplaatsNaar = useCallback((item, datum) => {
    if (!item || !datum) return;
    if (item.__pl) verzetPlDeadline(item.__pl, datum);
    else moveToDate(item, datum);
  }, [verzetPlDeadline, moveToDate]);

  const sluitDatePicker = () => { setDatePickerAnchor(null); setDatePickerItem(null); };

  const achterstalligActies = {
    onVandaag: (rij) => (rij.soort === 'eigen' ? moveToToday(rij.taak) : verzetPlDeadline(rij, todayStr())),
    onNieuweDatum: (rij, anchor) => (rij.soort === 'eigen'
      ? openDatePicker(rij.taak, anchor)
      : (setDatePickerItem({ __pl: rij, startDate: tomorrowStr() }), setDatePickerValue(tomorrowStr()), setDatePickerAnchor(anchor))),
    onAfgerond: (rij) => (rij.soort === 'eigen' ? rondEigenAf(rij.taak) : rondPlAf(rij)),
    onOpen: (rij, e) => (rij.soort === 'eigen'
      ? handleClickTask(rij.taak, e)
      : openDeadline(rij.dl, e)),
  };

  // ─── Bronnen (backlog / To Do / acties) ──────────────────────────────────

  const q = searchQuery.toLowerCase();
  const filteredBacklog = useMemo(() =>
    backlogTasks
      .filter(t => !t.title.toLowerCase().startsWith('reistijd'))
      .filter(t => !isAchterstallig(t, vandaag))
      .filter(t => !q || t.title.toLowerCase().includes(q) || t.projectName?.toLowerCase().includes(q)),
  [backlogTasks, q, vandaag]);
  const filteredMsTodos = useMemo(() =>
    msTodos.filter(t => !q || t.title.toLowerCase().includes(q)),
  [msTodos, q]);
  const filteredActions = useMemo(() =>
    actionItems.filter(t => !q || t.title.toLowerCase().includes(q) || t.projectName?.toLowerCase().includes(q)),
  [actionItems, q]);
  const backlogAantal = backlogTasks.filter(t => !t.title.toLowerCase().startsWith('reistijd') && !isAchterstallig(t, vandaag)).length;

  // Backlog: deze week → volgende week → later → geen datum (achterstallig staat links).
  const groupedBacklog = useMemo(() => {
    const thisWeek = [];
    const nextWeek = [];
    const noDate = [];
    const rest = [];
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const daysToNextSunday = daysToSunday + 7;
    filteredBacklog.forEach(t => {
      if (!t.startDate && !t.deadline) noDate.push(t);
      else {
        const dl = Math.max(0, daysUntil(t.startDate || t.deadline));
        if (dl <= daysToSunday) thisWeek.push(t);
        else if (dl <= daysToNextSunday) nextWeek.push(t);
        else rest.push(t);
      }
    });
    return { thisWeek, nextWeek, noDate, rest };
  }, [filteredBacklog]);

  const sortedTomorrow = useMemo(() => {
    const byTime = (a, b) => (a.startTime && b.startTime ? new Date(a.startTime) - new Date(b.startTime) : a.startTime ? -1 : b.startTime ? 1 : 0);
    return [...tomorrowTasks].sort(byTime);
  }, [tomorrowTasks]);
  const morgenAgenda = useMemo(() => calEvents.tomorrow.filter((ev) => !tomorrowTasks.some((t) => zelfdeAfspraak(t, ev))), [calEvents.tomorrow, tomorrowTasks]);
  const morgenMin = tomorrowTasks.filter((t) => !t.startTime).reduce((s, t) => s + taakMinuten(t, {}).min, 0);

  // ─── Slepen ──────────────────────────────────────────────────────────────

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const tijdlijnRef = useRef(null);

  // Alleen wat écht onder de muis ligt telt (anders "vangt" de dichtstbijzijnde
  // zone een drop in het niets). Een taakregel wint van de zone eromheen.
  const botsing = useCallback((args) => {
    const raak = pointerWithin(args);
    const regels = raak.filter((c) => c.id !== 'today-zone' && c.id !== 'tomorrow-zone');
    return regels.length ? regels : raak;
  }, []);

  const handleDragStart = (event) => setDragActive(event.active.data?.current?.item || null);

  const uurOpTijdlijn = (event, bron) => {
    const rect = tijdlijnRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const top = bron === 'tijdlijn'
      ? event.active.rect.current?.translated?.top
      : (event.activatorEvent?.clientY ?? 0) + event.delta.y - 10;
    if (top == null) return null;
    const uur = snapKwartier(TL_VAN + (top - rect.top) / TL_PXH);
    return Math.max(TL_VAN, Math.min(TL_TOT - 0.25, uur));
  };

  const handleDragEnd = (event) => {
    setDragActive(null);
    const { active, over } = event;
    if (!over) return;
    const item = active.data?.current?.item;
    const bron = active.data?.current?.bron;
    if (!item) return;
    const overBron = over.data?.current?.bron;

    if (over.id === 'tijdlijn') {
      const uur = uurOpTijdlijn(event, bron);
      if (uur == null) return;
      if (bron === 'vandaag' || bron === 'tijdlijn') { zetTijd(item.id, uur); return; }
      if (bron === 'morgen') moveFromTomorrowToToday(item);
      else moveToToday(item);
      if (item.source === 'airtable') zetTijd(item.id, uur);
      return;
    }
    if (over.id === 'tomorrow-zone' || over.id === 'tab-morgen') {
      if (bron === 'vandaag' || bron === 'tijdlijn') { zetTijd(item.id, null); moveToTomorrow(item); }
      else if (bron !== 'morgen') moveToDate(item, tomorrowStr());
      return;
    }
    if (over.id === 'today-zone' || overBron === 'vandaag') {
      if (bron === 'vandaag') {
        if (overBron === 'vandaag' && over.id !== active.id) {
          const ids = dagTaken.map((t) => t.id);
          const van = ids.indexOf(active.id);
          const naar = ids.indexOf(over.id);
          if (van !== -1 && naar !== -1) bewaarPlan((p) => ({ ...p, volgorde: arrayMove(ids, van, naar) }));
        }
      } else if (bron === 'tijdlijn') {
        zetTijd(item.id, null); // terug naar de lijst = tijdblok los
      } else if (bron === 'morgen') {
        moveFromTomorrowToToday(item);
      } else {
        moveToToday(item);
      }
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  const overMin = geplandMin - vrijMin;
  const naarMorgenIds = teVerschuiven(
    naarMorgenKandidaten(dagTaken).map((t) => ({ id: t.id, min: minutenVan(t).min, vast: plan.tijden[t.id] ?? null })), overMin);
  const naarMorgen = dagTaken.filter((t) => naarMorgenIds.includes(t.id));
  const namenLijst = (namen) => (namen.length <= 1 ? namen.join('')
    : `${namen.slice(0, -1).join(', ')} en ${namen[namen.length - 1]}`);
  // "N achterstallig" in de kop: klapt de sectie open, scrolt ernaartoe en zet de focus op
  // de kop (wiki 27 sep: de ref hing nergens aan, dus de klik deed niets zichtbaars).
  const openAchterstallig = () => {
    setAchterstalligOpen(true);
    setTimeout(() => {
      const doel = achterstalligRef.current;
      if (!doel) return;
      doel.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      doel.querySelector('[aria-expanded]')?.focus({ preventScroll: true });
    }, 50);
  };
  const bronRij = (item, bron) => (
    <SleepbareRij key={item.id} item={item} bron={bron} compact vandaag={vandaag}
      onVandaag={moveToToday} onDatum={openDatePicker}
      onKlik={item.source === 'airtable' ? handleClickTask : undefined} />
  );
  const backlogGroep = (label, lijst) => lijst.length > 0 && (
    <>
      <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, px: 0.5, mt: 0.75 }}>
        {label} ({lijst.length})
      </Typography>
      {lijst.map((item) => bronRij(item, 'backlog'))}
    </>
  );

  const opleveringen = todayDeadlines
    .filter((d) => showInternalDeadlines || !isInternProject(d.projectName))
    .filter((d) => !(d.type === 'task' && !d.deliverableId));

  return (
    <DndContext sensors={sensors} collisionDetection={botsing} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDragActive(null)}>
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 2, md: 3 }, pb: 10, backgroundColor: 'transparent' }}>
        <PageHeader
          dense
          icon="today"
          title="Vandaag"
          subtitle={`${longDate()} — ${userName.split(' ')[0]}`}
          rightActions={
            <>
              {achterstalligRijen.length > 0 && (
                <Box component="button" type="button" onClick={openAchterstallig} sx={{
                  all: 'unset', cursor: 'pointer', fontSize: MAAT.klein, color: KLEUR.zacht,
                  '&:hover, &:focus-visible': { color: KLEUR.primair, textDecoration: 'underline' },
                }}>{achterstalligRijen.length} achterstallig</Box>
              )}
              {/* Het widgetbord was de startpagina en staat nu onder /home (26 sep, besluit
                  Joris): één stille link in de kop, zodat wie zijn widgets zoekt ze vindt. */}
              <Box component={RouterLink} to="/home" sx={{
                fontSize: MAAT.klein, color: KLEUR.zacht, textDecoration: 'none',
                '&:hover, &:focus-visible': { color: KLEUR.primair, textDecoration: 'underline' },
              }}>Mijn widgets</Box>
              <IconButton onClick={refreshAll} size="small" aria-label="Vernieuwen" sx={{ color: KLEUR.stil, '&:hover': { color: KLEUR.primair } }}>
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          }
        />

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>

          {/* ═══ Dagkolom ═══ */}
          <Box sx={{ flex: '1 1 440px', minWidth: 0, width: { xs: '100%', md: 'auto' }, maxWidth: { md: 640 } }}>
            {/* Optelling: gepland vs vrij (van nu tot einde werkdag) — de ene vraag van dit scherm */}
            <Box sx={{ mb: 2 }}>
              <Tooltip enterDelay={500} placement="bottom-start" title={`${rest.heleDag ? `Werkdag ${fmtUur(WERKDAG.start)}–${fmtUur(WERKDAG.eind)}` : rest.eind > rest.start ? `Vanaf ${fmtUur(rest.start)} tot ${fmtUur(rest.eind)}` : `Na ${fmtUur(WERKDAG.eind)}`}${rest.eind > rest.start ? (aantalAfspraken > 0 ? `, min ${aantalAfspraken} ${aantalAfspraken === 1 ? 'afspraak' : 'afspraken'} (${fmtDuur(afspraakMin)})` : ', geen afspraken meer') : ''}`}>
                <Typography sx={{ fontSize: MAAT.tekst, color: KLEUR.zacht, fontVariantNumeric: 'tabular-nums', display: 'inline-block' }}>
                  <Box component="span" sx={{ color: KLEUR.tekst, fontWeight: 600 }}>{fmtDuur(geplandMin)} gepland</Box>
                  {rest.eind > rest.start
                    ? ` · ${fmtDuur(vrijMin)} vrij ${rest.heleDag ? 'vandaag' : `tot ${fmtUur(rest.eind)}`}`
                    : ' · werkdag voorbij'}
                </Typography>
              </Tooltip>
              <LinearProgress
                variant="determinate"
                value={vrijMin > 0 ? Math.min(100, (geplandMin / vrijMin) * 100) : (geplandMin > 0 ? 100 : 0)}
                sx={{
                  mt: 0.75, height: 3, borderRadius: 2, backgroundColor: '#eceff2',
                  '& .MuiLinearProgress-bar': { borderRadius: 2, backgroundColor: KLEUR.primair },
                }}
              />
              {overMin > 0 && (
                <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, mt: 0.75 }}>
                  <Box component="span" sx={{ color: KLEUR.telaat }}>{fmtDuur(overMin)} te veel</Box>
                  {naarMorgen.length > 0 && (
                    <>
                      {' — '}verschuif {namenLijst(naarMorgen.map((t) => `'${korteTitel(t.title, t.projectName)}'`))} naar {morgenNaam()}{' '}
                      <Box component="button" type="button" onClick={() => schuifNaarMorgen(naarMorgen)} sx={{
                        all: 'unset', cursor: 'pointer', color: KLEUR.primair, fontWeight: 600, ml: 0.5,
                        '&:hover, &:focus-visible': { textDecoration: 'underline' },
                      }}>
                        Verschuif
                      </Box>
                    </>
                  )}
                </Typography>
              )}
            </Box>

            {/* Overleg-voorbereiding: open invulblok als taakregel (alleen voor deelnemers, weg na indienen) */}
            <OverlegTaak />

            {/* Snel toevoegen — stil tot je erin klikt */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, mb: 0.5, borderRadius: '8px', '&:focus-within': { backgroundColor: '#f7f8fa' } }}>
              <AddIcon sx={{ color: KLEUR.stil, fontSize: 20 }} />
              <InputBase
                fullWidth
                placeholder="Taak toevoegen"
                value={quickAddText}
                onChange={(e) => setQuickAddText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && quickAdd()}
                inputProps={{ 'aria-label': 'Taak toevoegen aan vandaag' }}
                sx={{ fontSize: MAAT.tekst, color: KLEUR.tekst, '& input::placeholder': { color: KLEUR.zacht, opacity: 1 } }}
              />
              {quickAddText && (
                <Button size="small" onClick={quickAdd} sx={{ textTransform: 'none', fontSize: MAAT.klein, minWidth: 0 }}>Toevoegen</Button>
              )}
            </Box>

            {/* Takenlijst van vandaag */}
            <DropZone id="today-zone">
              {loading.tasks ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>
              ) : dagTaken.length === 0 ? (
                <Box sx={{ py: 3, px: 1 }}>
                  <Typography sx={{ color: KLEUR.zacht, fontSize: MAAT.tekst }}>Nog niets gepland voor vandaag.</Typography>
                  <Typography sx={{ color: KLEUR.zacht, fontSize: MAAT.klein, mt: 0.25 }}>
                    Typ een taak, of sleep er een uit Achterstallig of de lijsten rechts hierheen.
                  </Typography>
                </Box>
              ) : (
                <SortableContext items={dagTaken.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {dagTaken.map((item) => (
                      <SorteerbareRij
                        key={item.id} item={item} vandaag={vandaag}
                        minuten={minutenVan(item)}
                        blok={blokVan.get(item.id)} werkdagEind={WERKDAG.eind}
                        onToggle={toggleComplete} onMoveDay={(it) => { zetTijd(it.id, null); moveToTomorrow(it); }} moveDayDirection="tomorrow" morgenLabel={morgenNaam()}
                        onKlik={handleClickTask} onDatum={openDatePicker} onDuur={zetDuur}
                      />
                    ))}
                  </Box>
                </SortableContext>
              )}
            </DropZone>
            <Box sx={{ height: 16 }} />

            {/* Opleveringen & deliverable-deadlines van vandaag */}
            {opleveringen.length > 0 && (
              <Sectie titel={`${opleveringen.length} ${opleveringen.length === 1 ? 'deadline' : 'deadlines'} vandaag`}>
                {groepeerOpProject(opleveringen).map((g) => (
                  <Box key={g.project || '-'} sx={{ mb: 0.75 }}>
                    <Box sx={{ py: 0.25 }}>
                      {g.project ? <ProjectTag naam={g.project} maxWidth={420} /> : (
                        <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht }}>Zonder project</Typography>
                      )}
                    </Box>
                    {g.items.map((d) => (
                      <SectieRegel key={d.id} titel={korteTitel(d.name, d.projectName)}
                        rechts={(
                          <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, flexShrink: 0, whiteSpace: 'nowrap' }}>
                            {d.type === 'deliverable' ? `deliverable · ${(d.deadlineType || 'intern').toLowerCase()}` : 'oplevering'}
                            {d.assignee ? ` · ${d.assignee}` : ''}
                          </Typography>
                        )}
                        onClick={(e) => openDeadline(d, e)} />
                    ))}
                  </Box>
                ))}
              </Sectie>
            )}

            {/* Afgerond vandaag */}
            {afgerondVandaag.length > 0 && (
              <Sectie titel={`${afgerondVandaag.length} afgerond vandaag`}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {afgerondVandaag.map((item) => (
                    <RijInhoud key={item.id} item={item} compact vandaag={vandaag}
                      onToggle={toggleComplete} onKlik={handleClickTask} onMarkSent={markAsSent} />
                  ))}
                </Box>
              </Sectie>
            )}

            {/* PL-todo's */}
            {plTodos.length > 0 && (
              <Sectie titel={`${plTodos.length} PL-todo${plTodos.length === 1 ? '' : "'s"}`}>
                {plTodos.map((t) => (
                  <SectieRegel key={t.id} titel={t.titel}
                    sub={`${t.taskName || ''}${t.projectNumber ? ` · ${t.projectNumber}` : ''}${t.assignee ? ` · ${t.assignee}` : ''}`}
                    rechts={t.wanneer && <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, flexShrink: 0 }}>{t.wanneer}</Typography>}
                    onClick={() => openPlTodo(t)} />
                ))}
              </Sectie>
            )}

            {/* Overleg deze week */}
            {overleg && (
              <Sectie titel={`${(overleg.sessies || []).length} overleg${(overleg.sessies || []).length === 1 ? '' : 'gen'} deze week${(overleg.niet_gehouden || []).length ? ` · ${overleg.niet_gehouden.length} niet gehouden` : ''}`}
                standaardOpen={(overleg.niet_gehouden || []).length > 0}
                rechts={<Button size="small" onClick={(e) => { e.stopPropagation(); navigate('/overleg'); }} sx={{ textTransform: 'none', minWidth: 0, fontSize: MAAT.klein, py: 0 }}>Mijn week →</Button>}>
                <NietGehouden items={overleg.niet_gehouden} onVeranderd={laadOverleg} compact />
                {(overleg.sessies || []).map((s) => (
                  <SectieRegel key={s.id} titel={s.type_naam}
                    sub={`${new Date(s.gepland_start_nl).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} ${new Date(s.gepland_start_nl).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}${s.prep_nodig ? (s.prep_ingediend ? ' · ✓ ingevuld' : ' · invulblok open') : ''}`}
                    onClick={() => navigate(`/overleg/sessie/${s.id}`)} />
                ))}
                {((overleg.todos || []).length > 0 || overleg.te_laat > 0) && (
                  <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, mt: 0.5 }}>
                    {(overleg.todos || []).length} open actie(s) uit overleggen
                    {overleg.te_laat > 0 ? `, waarvan ${overleg.te_laat} te laat` : ''}
                    {(overleg.in_te_vullen || []).length ? ` · ${overleg.in_te_vullen.length} cijfer(s) in te vullen` : ''}
                  </Typography>
                )}
              </Sectie>
            )}

            {/* Achterstallig — de ref is het doel van "N achterstallig" in de kop */}
            <Box ref={achterstalligRef} sx={{ scrollMarginTop: 72 }}>
              <Achterstallig
                rijen={achterstalligRijen}
                open={achterstalligOpen}
                onToggleOpen={() => setAchterstalligOpen((o) => !o)}
                {...achterstalligActies}
              />
            </Box>
            {interneDeadlines > 0 && (
              <Box component="button" type="button" onClick={() => setShowInternalDeadlines((v) => !v)} sx={{
                all: 'unset', cursor: 'pointer', display: 'block', mt: 0.75, px: 0.5, fontSize: MAAT.klein, color: KLEUR.zacht,
                '&:hover, &:focus-visible': { color: KLEUR.primair, textDecoration: 'underline' },
              }}>
                {showInternalDeadlines ? 'Verberg interne projecten' : `Toon interne projecten (${interneDeadlines})`}
              </Box>
            )}
          </Box>

          {/* ═══ Tijdlijn ═══ */}
          <Box
            title="Sleep een taak op een tijd om hem vast te zetten; gestippeld = voorstel in lijstvolgorde. Tijdblokken en volgorde bewaart deze browser."
            sx={{ flex: '0 1 280px', minWidth: 220, width: { xs: '100%', md: 'auto' }, position: { md: 'sticky' }, top: { md: 16 } }}>
            {/* Uitleg zit in de native tooltip — geen extra tekst op het scherm */}
            <DagTijdlijn
              afspraken={afspraken}
              blokken={blokken}
              nu={nu}
              onKlikTaak={handleClickTask}
              meetRef={tijdlijnRef}
              agendaFout={agendaFout}
            />
          </Box>

          {/* ═══ Morgen / bronnen: dicht tot je erom vraagt (icoonrail rechts) ═══ */}
          {paneelOpen && (
          <Box sx={{ flex: '0 1 300px', minWidth: 260, width: { xs: '100%', md: 'auto' } }}>
            <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${KLEUR.lijn}`, overflow: 'hidden', backgroundColor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="fullWidth"
                sx={{
                  minHeight: 42, borderBottom: `1px solid ${KLEUR.lijn}`,
                  flex: 1,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: MAAT.klein, minHeight: 40, px: 0.25, minWidth: 0, color: KLEUR.zacht },
                  '& .Mui-selected': { color: `${KLEUR.tekst} !important` },
                  '& .MuiTabs-indicator': { backgroundColor: KLEUR.primair },
                }}
              >
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>{MorgenHoofd()}<Teller n={tomorrowTasks.length} /></Box>} />
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>Backlog<Teller n={backlogAantal} /></Box>} />
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>To&nbsp;Do<Teller n={msTodos.length} /></Box>} />
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>Acties<Teller n={actionItems.length} /></Box>} />
              </Tabs>
              <IconButton size="small" aria-label="Paneel sluiten" onClick={() => setPaneelOpen(false)} sx={{ mx: 0.5, color: KLEUR.stil }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              </Box>

              {activeTab !== 0 && (
                <Box sx={{ p: 1.25, borderBottom: `1px solid ${KLEUR.lijn}` }}>
                  <TextField
                    fullWidth
                    placeholder="Zoeken…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: KLEUR.stil }} /></InputAdornment>,
                      sx: { borderRadius: '8px', fontSize: MAAT.tekst, height: 34 },
                    }}
                  />
                </Box>
              )}

              <Box sx={{ maxHeight: { md: 'calc(100vh - 280px)' }, overflowY: 'auto', p: 1.25 }}>
                {/* Morgen */}
                {activeTab === 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 0.5, mb: 1 }}>
                      <Typography sx={{ fontSize: MAAT.tekst, fontWeight: 600, color: KLEUR.tekst }}>{tomorrowLongDate()}</Typography>
                      <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht }}>
                        {tomorrowTasks.length} {tomorrowTasks.length === 1 ? 'taak' : 'taken'}{morgenMin > 0 ? ` · ${fmtDuur(morgenMin)}` : ''}
                      </Typography>
                    </Box>
                    <DropZone id="tomorrow-zone" minHeight={sortedTomorrow.length || morgenAgenda.length ? 80 : 40}>
                      {loading.tasks ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                      ) : sortedTomorrow.length === 0 && morgenAgenda.length === 0 ? (
                        <Typography sx={{ color: KLEUR.stil, fontSize: MAAT.klein, px: 0.5, py: 1 }}>
                          Leeg — sleep een taak hierheen of op het tabblad
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {morgenAgenda.map((ev) => (
                            <Box key={ev.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 1, py: 0.5, borderLeft: `3px solid ${KLEUR.stil}`, backgroundColor: KLEUR.achter, borderRadius: '6px' }}>
                              <Typography sx={{ fontSize: MAAT.klein, fontWeight: 700, color: KLEUR.zacht, fontVariantNumeric: 'tabular-nums' }}>{String(ev.start).slice(11, 16)}</Typography>
                              <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.tekst, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.subject}</Typography>
                            </Box>
                          ))}
                          {sortedTomorrow.map((item) => (
                            <SleepbareRij key={item.id} item={item} bron="morgen" compact vandaag={vandaag}
                              minuten={item.startTime ? null : taakMinuten(item, {})}
                              begintijd={item.startTime ? uurVan(item.startTime) : null}
                              onToggle={toggleComplete} onMoveDay={moveFromTomorrowToToday} moveDayDirection="today"
                              onKlik={handleClickTask} onDatum={openDatePicker} />
                          ))}
                        </Box>
                      )}
                    </DropZone>
                  </>
                )}

                {/* Backlog */}
                {activeTab === 1 && (
                  <>
                    <TextField
                      fullWidth
                      placeholder="Nieuwe taak in backlog…"
                      value={backlogQuickAdd}
                      onChange={(e) => setBacklogQuickAdd(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && quickAddBacklog()}
                      size="small"
                      sx={{ mb: 1 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><AddIcon sx={{ fontSize: 18, color: KLEUR.primair }} /></InputAdornment>,
                        sx: { borderRadius: '8px', fontSize: MAAT.tekst, height: 34 },
                      }}
                    />
                    {eigenAchterstallig.length > 0 && (
                      <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, px: 0.5, mb: 0.5 }}>
                        {eigenAchterstallig.length} achterstallige {eigenAchterstallig.length === 1 ? 'taak staat' : 'taken staan'} links onder Achterstallig.
                      </Typography>
                    )}
                    {loading.tasks ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                    ) : filteredBacklog.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography sx={{ color: KLEUR.zacht, fontSize: MAAT.tekst }}>Geen openstaande taken</Typography>
                        <Typography sx={{ color: KLEUR.stil, display: 'block', mt: 0.5, fontSize: MAAT.klein }}>
                          Backlog toont je taken die niet voor vandaag of de volgende werkdag gepland staan
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {backlogGroep('Deze week', groupedBacklog.thisWeek)}
                        {backlogGroep('Volgende week', groupedBacklog.nextWeek)}
                        {backlogGroep('Later', groupedBacklog.rest)}
                        {backlogGroep('Geen datum', groupedBacklog.noDate)}
                      </Box>
                    )}
                  </>
                )}

                {/* Microsoft To Do */}
                {activeTab === 2 && (
                  <>
                    <TextField
                      fullWidth
                      placeholder="Nieuwe Microsoft To Do…"
                      value={todoQuickAdd}
                      onChange={(e) => setTodoQuickAdd(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && quickAddTodo()}
                      size="small"
                      disabled={!msListId}
                      sx={{ mb: 1 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><AddIcon sx={{ fontSize: 18, color: KLEUR.primair }} /></InputAdornment>,
                        sx: { borderRadius: '8px', fontSize: MAAT.tekst, height: 34 },
                      }}
                    />
                    {loading.mstodo ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                    ) : filteredMsTodos.length === 0 ? (
                      <Typography sx={{ color: KLEUR.zacht, textAlign: 'center', py: 3, fontSize: MAAT.tekst }}>Geen Microsoft To Do-taken</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {filteredMsTodos.map((item) => bronRij(item, 'mstodo'))}
                      </Box>
                    )}
                  </>
                )}

                {/* Actiepunten */}
                {activeTab === 3 && (
                  loading.actions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  ) : filteredActions.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography sx={{ color: KLEUR.zacht, fontSize: MAAT.tekst }}>Geen actiepunten gevonden</Typography>
                      <Typography sx={{ color: KLEUR.stil, display: 'block', mt: 0.5, fontSize: MAAT.klein }}>
                        Acties worden automatisch uit vergadernotulen gehaald wanneer je naam erbij staat
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {filteredActions.map((item) => bronRij(item, 'actie'))}
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          </Box>
          )}

          {/* Icoonrail: Morgen (ook sleepdoel), Backlog, To Do, Acties */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 0.5, flexShrink: 0 }}>
            {[
              { idx: 0, Icon: MorgenIcon, label: MorgenHoofd(), n: tomorrowTasks.length, drop: true },
              { idx: 1, Icon: BacklogIcon, label: 'Backlog', n: backlogAantal },
              { idx: 2, Icon: TodoIcon, label: 'Microsoft To Do', n: msTodos.length },
              { idx: 3, Icon: NoteIcon, label: 'Actiepunten', n: actionItems.length },
            ].map(({ idx, Icon, label, n, drop }) => {
              const actief = paneelOpen && activeTab === idx;
              const knop = (
                <Tooltip key={idx} title={`${label}${n ? ` (${n > 99 ? '99+' : n})` : ''}${drop ? ' — sleep een taak hierheen' : ''}`} placement="left">
                  <IconButton
                    aria-label={label}
                    aria-pressed={actief}
                    onClick={() => { if (actief) setPaneelOpen(false); else { setActiveTab(idx); setPaneelOpen(true); } }}
                    sx={{ width: 36, height: 36, borderRadius: '8px', color: actief ? KLEUR.primair : KLEUR.stil, backgroundColor: actief ? '#eef1f5' : 'transparent', '&:hover': { color: KLEUR.primair, backgroundColor: '#f2f4f6' } }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              );
              return drop ? <DropTabLabel key={idx} id="tab-morgen">{knop}</DropTabLabel> : knop;
            })}
          </Box>
        </Box>
      </Box>

      {/* Drag Overlay */}
      <DragOverlay dropAnimation={null}>
        {dragActive && <Box sx={{ width: 360 }}><RijInhoud item={dragActive} isOverlay compact minuten={dragActive.source === 'airtable' ? taakMinuten(dragActive, plan.duur) : null} /></Box>}
      </DragOverlay>

      {/* ═══ Deadline-popover (PL-deadlines en opleveringen) ═══ */}
      <Popover
        open={Boolean(dlAnchor)}
        anchorEl={dlAnchor}
        onClose={() => { setDlAnchor(null); setSelectedDl(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 340, borderRadius: 2 } } }}
      >
        {selectedDl && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
              {selectedDl.name}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#767c7e', mb: 0.5 }}>
              {selectedDl.projectNumber ? `${selectedDl.projectNumber}. ` : ''}{selectedDl.projectName?.replace(/^\d+\.\s*/, '')}
              {selectedDl.assignee ? ` · ${selectedDl.assignee}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Chip label={new Date(selectedDl.deadline).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', ...(new Date(selectedDl.deadline).getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) })} size="small" sx={{ fontSize: '0.7rem' }} />
              {selectedDl.deadlineType && <Chip label={selectedDl.deadlineType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
            </Box>
            <Button
              size="small"
              variant="contained"
              fullWidth
              disableElevation
              onClick={() => { navigate(projectUrlVoorDeadline(selectedDl)); setDlAnchor(null); }}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Ga naar project →
            </Button>
          </Box>
        )}
      </Popover>

      {/* ═══ Taakdetail: het gedeelde rustige zijpaneel ═══ */}
      <TaakPaneel
        taak={selectedTask}
        open={Boolean(selectedTask)}
        onClose={() => { plKlik.current += 1; setSelectedTask(null); setPaneelFocus(null); }}
        onBijgewerkt={(taakId, velden) => {
          naPaneelWijziging(taakId, velden);
          // Deadline-lijsten (vandaag/achterstallig) volgen status, datum en afvinken.
          if (selectedTask?._bron === 'deadline' || ['Deadline', 'PrioNr', 'Afgerond', 'EndDateUitvoerder'].some((k) => k in velden)) fetchDeadlines();
        }}
        onLokaal={naFeedbackLokaal}
        focusVeld={paneelFocus}
        onVerwijderd={(id) => {
          const weg = (prev) => prev.filter((t) => t._airtableId !== id);
          setTodayTasks(weg); setTomorrowTasks(weg); setBacklogTasks(weg);
          fetchDeadlines();
        }}
        onNaarProject={(pid) => { setSelectedTask(null); navigate(`/projects/${pid}`); }}
        extraActies={selectedTask && plan.tijden[selectedTask.id] != null
          ? [{ label: `Tijdblok om ${fmtUur(plan.tijden[selectedTask.id])} wissen`, onClick: () => zetTijd(selectedTask.id, null) }]
          : []}
      />

      {/* ═══ Date Picker Popover ═══ */}
      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={sluitDatePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, borderRadius: 2 } } }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
          {datePickerItem?.__pl ? 'Nieuwe einddatum:' : 'Verplaats naar:'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
          <Chip label="Vandaag" size="small" sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
            onClick={() => { verplaatsNaar(datePickerItem, todayStr()); sluitDatePicker(); }} />
          <Chip label={MorgenHoofd()} size="small" sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
            onClick={() => { verplaatsNaar(datePickerItem, tomorrowStr()); sluitDatePicker(); }} />
        </Box>
        <TextField
          size="small"
          type="date"
          value={datePickerValue}
          onChange={(e) => setDatePickerValue(e.target.value)}
          InputProps={{ sx: { fontSize: '0.85rem', height: 36 } }}
          sx={{ mb: 1 }}
        />
        <Button
          size="small"
          variant="contained"
          fullWidth
          disableElevation
          onClick={() => { verplaatsNaar(datePickerItem, datePickerValue); sluitDatePicker(); }}
          sx={{ textTransform: 'none', fontSize: '0.8rem' }}
        >
          Verplaatsen
        </Button>
      </Popover>
    </DndContext>
  );
}
